import {FormEvent, useMemo, useState} from 'react'
import {Check, Eye, EyeOff, KeyRound, LockKeyhole, Save, ShieldCheck} from 'lucide-react'
import {sha256} from 'crypto-hash'

import * as API from '../../api'
import {getUserData} from '../auth'

const ProfileChangePasswordPage = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success')

  const hasMinimumLength = password.length >= 6
  const passwordsMatch = Boolean(confirmPassword) && password === confirmPassword
  const validationMessage = !password
    ? 'Informe a nova senha.'
    : !hasMinimumLength
    ? 'A nova senha deve conter no mínimo 6 caracteres.'
    : !confirmPassword
    ? 'Confirme a nova senha.'
    : !passwordsMatch
    ? 'A senha e a confirmação precisam ser iguais.'
    : ''

  const strength = useMemo(() => {
    if (!password) return 0
    let score = password.length >= 6 ? 1 : 0
    if (password.length >= 10) score += 1
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1
    if (/\d/.test(password) || /[^a-zA-Z0-9]/.test(password)) score += 1
    return score
  }, [password])
  const strengthLabel = ['Sem senha', 'Fraca', 'Razoável', 'Boa', 'Forte'][strength]

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (validationMessage || loading) return
    setLoading(true)
    setFeedback('')
    const gtag = (window as any).gtag
    gtag?.('event', 'submit', {
      event_category: 'Alterar Senha',
      event_label: 'Salvar nova senha',
    })

    try {
      const response = await API.changePassword({
        user_guid: getUserData().guid,
        password: await sha256(password),
      })
      if (response?.success === false) throw new Error('Password update failed')
      setPassword('')
      setConfirmPassword('')
      setFeedbackType('success')
      setFeedback('Senha alterada com sucesso.')
    } catch {
      setFeedbackType('error')
      setFeedback('Não foi possível alterar sua senha. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className='profile-form' onSubmit={handleSubmit}>
      <div className='profile-form__body'>
        <section className='profile-form__heading'>
          <span>
            <LockKeyhole size={18} />
          </span>
          <div>
            <h3>Alterar senha</h3>
            <p>Crie uma nova senha para proteger o acesso à sua conta.</p>
          </div>
        </section>

        <div className='profile-password__layout'>
          <section className='profile-security-card'>
            <span className='profile-security-card__icon'>
              <ShieldCheck size={24} />
            </span>
            <h3>Segurança da conta</h3>
            <p>
              Use uma senha exclusiva e evite repetir credenciais utilizadas em outros serviços.
            </p>
            <div className='profile-security-card__note'>
              <KeyRound size={14} />A nova senha passa a valer no seu próximo acesso.
            </div>
          </section>

          <section className='profile-password-card'>
            <div className='profile-field'>
              <label htmlFor='profile-new-password'>
                <LockKeyhole size={13} /> Nova senha
              </label>
              <div className='profile-password-input'>
                <input
                  id='profile-new-password'
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  className={password && !hasMinimumLength ? 'is-invalid' : ''}
                  autoComplete='new-password'
                  placeholder='Digite a nova senha'
                  onChange={(event) => {
                    setPassword(event.target.value)
                    setFeedback('')
                  }}
                />
                <button
                  type='button'
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => setShowPassword((current) => !current)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className='profile-strength' aria-label={`Força da senha: ${strengthLabel}`}>
              <div>
                <span>Força da senha</span>
                <strong>{strengthLabel}</strong>
              </div>
              <span className={`profile-strength__track is-${strength}`}>
                <i />
              </span>
            </div>

            <div className='profile-field'>
              <label htmlFor='profile-confirm-password'>
                <Check size={13} /> Confirmar nova senha
              </label>
              <div className='profile-password-input'>
                <input
                  id='profile-confirm-password'
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  className={confirmPassword ? (passwordsMatch ? 'is-valid' : 'is-invalid') : ''}
                  autoComplete='new-password'
                  placeholder='Repita a nova senha'
                  onChange={(event) => {
                    setConfirmPassword(event.target.value)
                    setFeedback('')
                  }}
                />
              </div>
            </div>

            <ul className='profile-password-requirements'>
              <li className={hasMinimumLength ? 'is-complete' : ''}>
                <span>{hasMinimumLength && <Check size={11} />}</span>
                Pelo menos 6 caracteres
              </li>
              <li className={passwordsMatch ? 'is-complete' : ''}>
                <span>{passwordsMatch && <Check size={11} />}</span>
                Senha e confirmação iguais
              </li>
            </ul>
          </section>
        </div>

        {feedback && (
          <div
            className={`profile-feedback ${
              feedbackType === 'error' ? 'profile-feedback--danger' : ''
            }`}
            role='status'
          >
            {feedback}
          </div>
        )}
      </div>

      <footer className='profile-form__footer'>
        <span>
          {password
            ? validationMessage || 'Sua nova senha está pronta.'
            : 'Nenhuma alteração pendente.'}
        </span>
        <button
          type='submit'
          className='ua-primary-button'
          disabled={Boolean(validationMessage || loading)}
        >
          {loading ? <span className='ua-spinner' /> : <Save size={14} />}
          Alterar senha
        </button>
      </footer>
    </form>
  )
}

export default ProfileChangePasswordPage
